import type { SourcingProjectSummary, StructuredQuery } from "@shared/labs/sourcing";
import { ArrowRight, FolderKanban, Loader2, Plus, UserRound, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { BriefStep } from "../brief-step";
import { EMPTY_LA_SCOPE, type LaScopeSelection } from "../model-picker";
import { QueryInterpretation } from "../query-summary";

/**
 * "Projetos" — one tracker per open position. The list shows the caller's
 * projects (candidate pool + shortlist + session counts). Creating one runs
 * the free `understand` pass on the JD and shows the STRUCTURED INTERPRETATION
 * for review (the same fact sheet the search flows show) before the project is
 * born with its canonical judge reference — no Apify spend at creation.
 */
export function ProjectsTab() {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [projects, setProjects] = useState<SourcingProjectSummary[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [brief, setBrief] = useState("");
  const [laScope, setLaScope] = useState<LaScopeSelection>(EMPTY_LA_SCOPE);
  const [query, setQuery] = useState<StructuredQuery | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    rpcClient.labs
      .sourcingProjectList({})
      .then((rows: SourcingProjectSummary[]) => {
        if (!cancelled) setProjects(rows);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function defaultProjectName(q: StructuredQuery): string {
    return q.filters.title?.trim() || brief.replace(/\s+/g, " ").trim().slice(0, 80) || "Nova vaga";
  }

  async function create() {
    if (!query || name.trim().length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const { projectId } = await rpcClient.labs.sourcingProjectCreate({
        name: name.trim(),
        query,
        laModelId: laScope.modelId ?? undefined,
        laDisciplineId: laScope.disciplineId ?? undefined,
        laLevel: laScope.level ?? undefined,
      });
      navigate(`/labs/sourcing/projects/${projectId}`);
    } catch (err) {
      setSubmitting(false);
      toast({
        title: "Falha ao criar o projeto",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    }
  }

  if (creating) {
    // Step 1 — the JD + the understand action (free LLM pass, no Apify).
    if (!query) {
      return (
        <div className="space-y-3">
          <BriefStep
            brief={brief}
            setBrief={setBrief}
            onParsed={(q) => {
              setQuery(q);
              setName((prev) => prev || defaultProjectName(q));
            }}
            laScope={laScope}
            setLaScope={setLaScope}
            enableLevel
          />
          <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>
            Cancelar
          </Button>
        </div>
      );
    }
    // Step 2 — review the structured interpretation (the project's canonical
    // judge reference) + name the project.
    return (
      <Card>
        <CardContent className="space-y-5 pt-5">
          <div>
            <QueryInterpretation
              query={query}
              eyebrow="Revise antes de criar"
              heading="Confira o que entendemos da vaga"
            />
          </div>
          <div className="border-t border-border pt-4">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              nome do projeto
            </div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Backend Sênior — Squad Pagamentos"
              maxLength={200}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={create} disabled={name.trim().length === 0 || submitting}>
              {submitting ? <Loader2 className="animate-spin" /> : <ArrowRight />}
              Criar projeto
            </Button>
            <Button variant="ghost" onClick={() => setQuery(null)} disabled={submitting}>
              Voltar ao brief
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Um projeto por vaga aberta: acumule candidatos de várias explorações e adições manuais, e
          faça a triagem em Sourced · Shortlist · Rejeitados.
        </p>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus /> Novo projeto
        </Button>
      </div>

      {projects === null && (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando projetos…
        </div>
      )}

      {projects?.length === 0 && (
        <section className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-12 text-center">
          <FolderKanban className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">Nenhum projeto ainda</p>
          <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
            Crie um projeto para a vaga que você está buscando — ou promova uma exploração já
            concluída em projeto direto da tela de resultados.
          </p>
        </section>
      )}

      {projects && projects.length > 0 && (
        <div className="space-y-2.5">
          {projects.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => navigate(`/labs/sourcing/projects/${p.id}`)}
              className="w-full rounded-xl border border-card-border bg-card p-4 text-left transition-colors hover:border-ring"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <span className="block truncate text-[15px] font-semibold">{p.name}</span>
                  <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Users size={12} />
                      {p.candidateCount} candidatos
                    </span>
                    <span>{p.shortlistedCount} na shortlist</span>
                    <span>
                      {p.sessionCount} {p.sessionCount === 1 ? "sessão" : "sessões"}
                    </span>
                    <span className="inline-flex min-w-0 items-center gap-1">
                      <UserRound size={12} className="shrink-0" />
                      <span className="truncate">{p.createdByName}</span>
                    </span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-mono text-[15px] font-semibold leading-none tabular-nums">
                    ${p.usd.toFixed(2)}
                  </div>
                  <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                    custo total
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
